import { ref, type Ref } from 'vue'

// Register global stubs so inline SVG onmouseover/onmouseout handlers don't throw
// The actual tooltip is handled by Vue event delegation
if (typeof window !== 'undefined') {
  ;(window as any).showToolTip = () => {}
  ;(window as any).hideToolTip = () => {}
  ;(window as any).highlightLinked = () => {}
  ;(window as any).highlightLinkedExtended = () => {}
  ;(window as any).setPathStyle = () => {}
  ;(window as any).resetPathStyle = () => {}
  ;(window as any).linkIDs = () => {}
  ;(window as any).unlinkIDs = () => {}
  ;(window as any).clickOnElement = () => {}
  ;(window as any).customMenuClick = () => {}
}

interface SvgInteractionOptions {
  containerRef: Ref<HTMLElement | null>
  onAreaClick: (id: string) => void
  onAreaHover: (id: string, name: string) => void
  onAreaLeave: () => void
}

export function useSvgInteraction(options: SvgInteractionOptions) {
  const scale = ref(1)
  const translateX = ref(0)
  const translateY = ref(0)
  const isDragging = ref(false)

  // Touch state
  let touchStartX = 0
  let touchStartY = 0
  let touchStartDist = 0
  let touchStartScale = 1
  let hasMoved = false
  let lastTapTime = 0

  const MIN_SCALE = 0.5
  const MAX_SCALE = 4
  const TAP_THRESHOLD = 10
  const DOUBLE_TAP_DELAY = 300

  function getTouchDistance(e: TouchEvent): number {
    if (e.touches.length < 2) return 0
    const dx = e.touches[0].clientX - e.touches[1].clientX
    const dy = e.touches[0].clientY - e.touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  function handleClick(e: MouseEvent) {
    const target = (e.target as Element).closest('a[id]')
    if (target) {
      e.preventDefault()
      e.stopPropagation()
      const id = target.getAttribute('id')
      if (id) options.onAreaClick(id)
    }
  }

  function extractTooltipName(el: Element): string {
    // RMI SVGs embed names in onmouseover="showToolTip('Part Name', event)"
    const attr = el.getAttribute('onmouseover') ?? ''
    const match = attr.match(/showToolTip\s*\(\s*'([^']+)'/)
    if (match) return match[1]
    // Fallback: <title> element, then ID
    return el.querySelector('title')?.textContent ?? el.getAttribute('id') ?? ''
  }

  function handleMouseOver(e: MouseEvent) {
    const target = (e.target as Element).closest('a[id]')
    if (target) {
      const id = target.getAttribute('id') ?? ''
      const name = extractTooltipName(target)
      options.onAreaHover(id, name)
      highlightElement(target, true)
    }
  }

  function handleMouseOut(e: MouseEvent) {
    const target = (e.target as Element).closest('a[id]')
    if (target) {
      highlightElement(target, false)
    }
    options.onAreaLeave()
  }

  function highlightElement(el: Element, active: boolean) {
    const paths = el.querySelectorAll('path, line, polyline, polygon, rect, circle, ellipse')
    paths.forEach(p => {
      if (active) {
        (p as SVGElement).style.stroke = '#00FF00'
        ;(p as SVGElement).style.strokeWidth = '2'
      } else {
        (p as SVGElement).style.stroke = ''
        ;(p as SVGElement).style.strokeWidth = ''
      }
    })
  }

  function handleTouchStart(e: TouchEvent) {
    hasMoved = false

    if (e.touches.length === 1) {
      touchStartX = e.touches[0].clientX - translateX.value
      touchStartY = e.touches[0].clientY - translateY.value
      isDragging.value = scale.value > 1
    } else if (e.touches.length === 2) {
      touchStartDist = getTouchDistance(e)
      touchStartScale = scale.value
    }
  }

  function handleTouchMove(e: TouchEvent) {
    if (e.touches.length === 1 && scale.value > 1) {
      // Pan
      translateX.value = e.touches[0].clientX - touchStartX
      translateY.value = e.touches[0].clientY - touchStartY
      hasMoved = true
    } else if (e.touches.length === 2) {
      // Pinch-to-zoom
      const dist = getTouchDistance(e)
      if (touchStartDist > 0) {
        const newScale = touchStartScale * (dist / touchStartDist)
        scale.value = Math.min(MAX_SCALE, Math.max(MIN_SCALE, newScale))
        hasMoved = true
      }
    }
  }

  function handleTouchEnd(e: TouchEvent) {
    isDragging.value = false

    if (!hasMoved && e.changedTouches.length === 1) {
      const now = Date.now()
      if (now - lastTapTime < DOUBLE_TAP_DELAY) {
        // Double-tap: toggle zoom
        if (scale.value > 1) {
          resetZoom()
        } else {
          const touch = e.changedTouches[0]
          zoomToPoint(touch.clientX, touch.clientY, 2.5)
        }
        lastTapTime = 0
        return
      }
      lastTapTime = now

      // Single tap — simulate click for SVG element detection
      const touch = e.changedTouches[0]
      const target = document.elementFromPoint(touch.clientX, touch.clientY)
      if (target) {
        const anchor = target.closest('a[id]')
        if (anchor) {
          const id = anchor.getAttribute('id')
          if (id) options.onAreaClick(id)
        }
      }
    }
  }

  function zoomToPoint(clientX: number, clientY: number, targetScale: number) {
    const container = options.containerRef.value
    if (!container) return

    const rect = container.getBoundingClientRect()
    const x = clientX - rect.left
    const y = clientY - rect.top

    scale.value = targetScale
    translateX.value = -(x * (targetScale - 1))
    translateY.value = -(y * (targetScale - 1))
  }

  function zoomIn() {
    const container = options.containerRef.value
    if (!container) return
    const rect = container.getBoundingClientRect()
    zoomToPoint(rect.left + rect.width / 2, rect.top + rect.height / 2, Math.min(MAX_SCALE, scale.value + 0.5))
  }

  function zoomOut() {
    scale.value = Math.max(MIN_SCALE, scale.value - 0.5)
    if (scale.value <= 1) resetZoom()
  }

  function resetZoom() {
    scale.value = 1
    translateX.value = 0
    translateY.value = 0
  }

  return {
    scale,
    translateX,
    translateY,
    isDragging,
    handleClick,
    handleMouseOver,
    handleMouseOut,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    zoomIn,
    zoomOut,
    resetZoom,
  }
}
